/**
 * SharedCredentialReports
 *
 * Public landing page for Share dialog "Security Type = Credentials":
 *   /#/infralytiqs/shared-reports/:shareId
 *
 * Recipients enter the shareable username/password. On success the server
 * issues an IL token (same store as the Infralytiqs SDK) and this page renders
 * the report suite with a lightweight branded header (domain logo + logout)
 * and a "Powered by Infralytiqs.com" mark. The Infralytiqs SDK embed is
 * unchanged and stays chrome-less.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createTheme } from '@mui/material/styles';
import {
  Alert, AppBar, Box, Button, CircularProgress, CssBaseline, IconButton,
  InputAdornment, Link, TextField, ThemeProvider, Toolbar, Tooltip, Typography,
  useMediaQuery,
} from '@mui/material';
import { Logout, Visibility, VisibilityOff } from '@mui/icons-material';
import { themeSettings, THEME_NAME_DEFAULT, THEME_NAME_PROFESSIONAL } from 'theme';
import {
  ThemePreference,
  cycleThemePreference,
  normalizeThemePreference,
} from 'theme/themeMode';
import { useResolvedThemeMode } from 'hooks/useResolvedThemeMode';
import apiClient, { SESSION_EXPIRED_EVENT } from 'utils/ApiClient';
import { SiteLogo } from 'components/SiteLogo';
import { ThemeModeToggle } from 'components/ThemeModeToggle/ThemeModeToggle';
import { IlTokenSuiteReports } from './IlTokenSuiteReports';

const shareTokenKey = (shareId: string) => `_il_share_token_${shareId}`;
const shareThemeKey = (shareId: string) => `_il_share_theme_${shareId}`;

interface PublicShare {
  _id: string;
  name: string;
  server: string;
  reportSuites: string[];
  defaultReportSuite?: string;
  securityType: string;
  theme?: string;
  themeType?: string;
}

const readStoredToken = (shareId: string): string => {
  try {
    return sessionStorage.getItem(shareTokenKey(shareId)) || '';
  } catch {
    return '';
  }
};

const persistToken = (shareId: string, tokenId: string) => {
  try {
    sessionStorage.setItem(shareTokenKey(shareId), tokenId);
  } catch {
    /* ignore quota / private mode */
  }
};

const clearStoredToken = (shareId: string) => {
  try {
    sessionStorage.removeItem(shareTokenKey(shareId));
  } catch {
    /* ignore */
  }
};

export const SharedCredentialReports: React.FC = () => {
  const { shareId = '' } = useParams<{ shareId: string }>();
  const [share, setShare] = useState<PublicShare | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loadingShare, setLoadingShare] = useState(true);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const [token, setToken] = useState<string>('');
  const [themePreference, setThemePreference] = useState<ThemePreference>('light');

  useEffect(() => {
    if (!shareId) {
      setLoadingShare(false);
      setLoadError('This shared report link is incomplete.');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoadingShare(true);
      setLoadError('');
      try {
        const res = await apiClient.get<PublicShare>(`/il/share/report-suites/public/${shareId}`);
        if (cancelled) return;
        const data = res?.data;
        if (!data?._id) {
          setLoadError('Shared report not found.');
          return;
        }
        setShare(data);
        const storedTheme = (() => {
          try {
            return sessionStorage.getItem(shareThemeKey(shareId));
          } catch {
            return null;
          }
        })();
        setThemePreference(normalizeThemePreference(storedTheme || data.themeType || 'LIGHT'));
        const existing = readStoredToken(shareId);
        if (existing) {
          apiClient.setIlToken(existing);
          setToken(existing);
        }
      } catch (err: any) {
        if (!cancelled) setLoadError(err?.message || 'Shared report not found.');
      } finally {
        if (!cancelled) setLoadingShare(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shareId]);

  const handleShareLogout = () => {
    clearStoredToken(shareId);
    apiClient.setIlToken(undefined);
    setToken('');
    setPassword('');
    setLoginError('');
  };

  useEffect(() => {
    const onExpired = () => {
      handleShareLogout();
      setLoginError('Your session expired. Please sign in again.');
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareId]);

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!shareId || !username.trim() || !password) return;
    setLoggingIn(true);
    setLoginError('');
    try {
      const res = await apiClient.post<{ tokenId: string; share?: PublicShare }>(
        `/il/share/report-suites/public/${shareId}/login`,
        { username: username.trim(), password },
      );
      const tokenId = res?.data?.tokenId;
      if (!tokenId) {
        setLoginError('Could not start a report session.');
        return;
      }
      persistToken(shareId, tokenId);
      apiClient.setIlToken(tokenId);
      if (res.data?.share) setShare(res.data.share);
      setPassword('');
      setToken(tokenId);
    } catch (err: any) {
      setLoginError(err?.message || 'Invalid username or password');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleCycleTheme = () => {
    const next = cycleThemePreference(themePreference);
    setThemePreference(next);
    try {
      sessionStorage.setItem(shareThemeKey(shareId), next);
    } catch {
      /* ignore quota / private mode */
    }
  };

  const mappedThemeName = (share?.theme || '').toUpperCase() === 'PROFESSIONAL'
    ? THEME_NAME_PROFESSIONAL
    : THEME_NAME_DEFAULT;
  const themeMode = useResolvedThemeMode(themePreference);
  const theme = useMemo(
    () => createTheme(themeSettings(themeMode, mappedThemeName)),
    [themeMode, mappedThemeName],
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SharedCredentialShell
        showLogout={Boolean(token)}
        onLogout={handleShareLogout}
        themePreference={themePreference}
        onCycleTheme={handleCycleTheme}
      >
        {loadingShare && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        )}

        {!loadingShare && loadError && (
          <Box sx={{ maxWidth: 480, mx: 'auto', mt: 8 }}>
            <Alert severity="error">{loadError}</Alert>
          </Box>
        )}

        {!loadingShare && share && !token && (
          <Box
            component="form"
            onSubmit={(e) => void handleLogin(e)}
            sx={{
              maxWidth: 420,
              mx: 'auto',
              mt: { xs: 3, md: 8 },
              p: 3,
              borderRadius: 2,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              boxShadow: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              {share.name || 'Shared report'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter the shareable credentials to open this report.
            </Typography>
            <TextField
              autoFocus
              size="small"
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setLoginError(''); }}
              autoComplete="username"
              disabled={loggingIn}
            />
            <TextField
              size="small"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
              autoComplete="current-password"
              disabled={loggingIn}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {loginError && <Alert severity="error">{loginError}</Alert>}
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={loggingIn || !username.trim() || !password}
              startIcon={loggingIn ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {loggingIn ? 'Signing in…' : 'Open report'}
            </Button>
          </Box>
        )}

        {!loadingShare && share && token && (
          <IlTokenSuiteReports
            server={share.server}
            reportSuites={share.reportSuites || []}
            defaultReportSuite={share.defaultReportSuite}
            token={token}
          />
        )}
      </SharedCredentialShell>
    </ThemeProvider>
  );
};

const SharedCredentialShell: React.FC<{
  showLogout: boolean;
  onLogout: () => void;
  themePreference: ThemePreference;
  onCycleTheme: () => void;
  children: React.ReactNode;
}> = ({ showLogout, onLogout, themePreference, onCycleTheme, children }) => {
  const isXs = useMediaQuery('(max-width: 600px)');

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
      color: 'text.primary',
    }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 1.5, sm: 2.5 },
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              '& img': {
                height: { xs: '36px !important', sm: '44px !important' },
                maxHeight: { xs: '36px !important', sm: '44px !important' },
                width: 'auto',
                maxWidth: { xs: '55vw', sm: 280 },
              },
              '& svg': {
                height: { xs: 36, sm: 44 },
                width: 'auto',
              },
            }}
          >
            <SiteLogo varient="small" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <ThemeModeToggle preference={themePreference} onCycle={onCycleTheme} />
            {showLogout && (
              isXs ? (
                <Tooltip title="Logout">
                  <IconButton
                    color="inherit"
                    onClick={onLogout}
                    aria-label="Logout"
                    size="small"
                  >
                    <Logout fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<Logout />}
                  onClick={onLogout}
                >
                  Logout
                </Button>
              )
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{
        flex: 1,
        p: { xs: 2, md: 3 },
        minWidth: 0,
      }}>
        {children}
      </Box>

      <Box sx={{
        px: { xs: 2, md: 3 },
        py: 1.25,
        textAlign: 'right',
        flexShrink: 0,
      }}>
        <Typography variant="caption" color="text.secondary">
          <Link
            href="https://infralytiqs.com"
            target="_blank"
            rel="noopener noreferrer"
            color="inherit"
            underline="hover"
          >
            Powered by Infralytiqs.com
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default SharedCredentialReports;
